/**
 * SupabaseSyncTarget — a SyncTarget that speaks Supabase PostgREST.
 *
 * No SDK, no lock-in: it uses plain REST against the `sync_records` table with
 * the standard `apikey` + bearer headers, so the exact same code also works
 * against self-hosted PostgREST or your own Postgres-backed API later. `fetch`
 * is injectable, so it is unit-testable with an in-memory PostgREST fake.
 *
 * Table contract (see supabase/schema.sql):
 *   sync_records(entity_id text pk, kind text, version int, privacy_class text,
 *                updated_at timestamptz, body jsonb)
 */

import type { EntityId, EntityKind, PrivacyClass } from '@life-os/app-core';
import type { RemoteRecordSnapshot } from './mutation.js';
import type { SyncRecord, SyncTarget } from './sync.js';
import type { FetchLike } from './sync-rest.js';

interface Row {
  entity_id: string;
  kind: string;
  version: number;
  privacy_class: string;
  updated_at: string;
  body: unknown;
}

function rowToRecord(r: Row): SyncRecord {
  return {
    entityId: r.entity_id as unknown as EntityId,
    kind: r.kind as EntityKind,
    version: r.version,
    privacyClass: r.privacy_class as PrivacyClass,
    updatedAt: r.updated_at,
    body: r.body,
  };
}

function recordToRow(rec: SyncRecord): Row {
  return {
    entity_id: String(rec.entityId),
    kind: rec.kind,
    version: rec.version,
    privacy_class: rec.privacyClass,
    updated_at: rec.updatedAt,
    body: rec.body,
  };
}

export interface SupabaseSyncOptions {
  /** Project URL, e.g. https://bmvvqhzduswplskvwxpz.supabase.co */
  readonly url: string;
  /** anon or service-role key. */
  readonly apiKey: string;
  readonly table?: string;
  readonly fetchImpl?: FetchLike;
}

export class SupabaseSyncTarget implements SyncTarget {
  readonly name = 'supabase';
  private readonly rest: string;
  private readonly apiKey: string;
  private readonly table: string;
  private readonly doFetch: FetchLike;

  constructor(opts: SupabaseSyncOptions) {
    this.rest = `${opts.url.replace(/\/$/, '')}/rest/v1`;
    this.apiKey = opts.apiKey;
    this.table = opts.table ?? 'sync_records';
    const injected = opts.fetchImpl;
    if (injected) this.doFetch = injected;
    else if (typeof globalThis.fetch === 'function') this.doFetch = (u, i) => globalThis.fetch(u, i) as unknown as ReturnType<FetchLike>;
    else throw new Error('SupabaseSyncTarget: no fetch available; pass fetchImpl.');
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return {
      'content-type': 'application/json',
      apikey: this.apiKey,
      authorization: `Bearer ${this.apiKey}`,
      ...extra,
    };
  }

  async get(entityId: EntityId): Promise<SyncRecord | null> {
    const res = await this.doFetch(
      `${this.rest}/${this.table}?entity_id=eq.${encodeURIComponent(String(entityId))}&select=*`,
      { method: 'GET', headers: this.headers() },
    );
    if (!res.ok) throw new Error(`Supabase get failed: ${res.status}`);
    const rows = (await res.json()) as Row[];
    return Array.isArray(rows) && rows.length > 0 ? rowToRecord(rows[0]!) : null;
  }

  async head(entityId: EntityId): Promise<RemoteRecordSnapshot | null> {
    const rec = await this.get(entityId);
    return rec ? { entityId, version: rec.version } : null;
  }

  async push(record: SyncRecord): Promise<void> {
    // Upsert on the primary key via PostgREST merge-duplicates.
    const res = await this.doFetch(`${this.rest}/${this.table}`, {
      method: 'POST',
      headers: this.headers({ prefer: 'resolution=merge-duplicates,return=minimal' }),
      body: JSON.stringify(recordToRow(record)),
    });
    if (!res.ok) throw new Error(`Supabase push failed: ${res.status}`);
  }

  async pull(since: string | null): Promise<readonly SyncRecord[]> {
    const filter = since === null ? '' : `updated_at=gt.${encodeURIComponent(since)}&`;
    const res = await this.doFetch(`${this.rest}/${this.table}?${filter}select=*`, {
      method: 'GET',
      headers: this.headers(),
    });
    if (!res.ok) throw new Error(`Supabase pull failed: ${res.status}`);
    const rows = (await res.json()) as Row[];
    return Array.isArray(rows) ? rows.map(rowToRecord) : [];
  }
}

/** Build a SupabaseSyncTarget from config values, or null when not configured. */
export function supabaseSyncFromConfig(
  cfg: { supabaseUrl: string | null; supabaseAnonKey: string | null },
  fetchImpl?: FetchLike,
): SupabaseSyncTarget | null {
  if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) return null;
  return new SupabaseSyncTarget({ url: cfg.supabaseUrl, apiKey: cfg.supabaseAnonKey, fetchImpl });
}
